/**
 * ConversationCommandPalette — Cmd+K (Ctrl+K) global palette.
 *
 * Phase 3.2 refresh (May 26, 2026 — Editor-first Canvas): migrated from
 * the in-file ``cmdk`` shell to the shared
 * ``@ando/ui::CommandPalette`` primitive. Every brand polish bit
 * (cream/orange selection, hairline borders, ⌘+Enter alt-select wiring,
 * group-header typography) now lives in the primitive — this file just
 * composes the lanes for the chat shell.
 *
 * Lanes:
 *   1. Azioni       — Nuova chat / Rinomina corrente / Esporta corrente
 *   2. Documenti    — top 20 recenti, ranking via cmdk fuzzy scoring.
 *                     ``Enter`` apre l'editor sul doc; ``⌘+Enter`` prefilla
 *                     il composer della chat con ``"modifica {filename}: "``
 *                     così l'utente può subito digitare la richiesta.
 *   3. Conversazioni — fuzzy by title; ``Enter`` switcha conv.
 *   4. Progetti     — switch the workspace project (placeholder until the
 *                     projects feature lands its setter).
 *
 * Keyboard contract handled by ``CommandPalette``:
 *   ⌘K / Ctrl+K toggles (via ``useChatCommandPalette``).
 *   ↑ ↓ navigate · Enter selects · ⌘+Enter alt-select · Esc closes.
 *
 * The Phase 3 ambient-edit goal is that "modifica X" no longer requires
 * the user to pre-open the document in the sidebar. The BE
 * ``resolve_document`` tool (Phase 3.1) does the resolution from a hint;
 * the Cmd+K palette is the FE escape hatch for the (rare) cases where the
 * user wants to open the editor first — either to skim before asking, or
 * to deep-link a precise filename into the next chat turn.
 */

import {
  CommandPalette,
  type CommandPaletteController,
  type CommandPaletteGroup,
  type CommandPaletteItem,
  useCommandPaletteShortcut as useGenericCommandPaletteShortcut,
} from "@ando/ui";
import { prompt } from "@ando/ui/dialog";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { useNavigate } from "@tanstack/react-router";
import {
  Download,
  FileSpreadsheet,
  FileText,
  Folder,
  MessageSquare,
  Pencil,
  Plus,
  Presentation,
  TrendingUp,
} from "lucide-react";
import { type FC, useCallback, useMemo, useState } from "react";

import { useCreateManualAction, useManualActionCatalog } from "@/features/actions";
import { selectSetCurrentProjectId, useProjectsStore } from "@/features/projects";
import { formatRelativeTime } from "@/features/workspace/hooks/formatRelativeTime";
import { useDocuments } from "@/features/workspace/hooks/useDocuments";
import { useCreateConversation } from "../hooks/useConversationMutations";
import { useExportConversation } from "../hooks/useExportConversation";
import { useSidebarProjects } from "../hooks/useSidebarProjects";
import { selectCurrentConversationId, useChatStore } from "../state/chatStore";

export interface ConversationCommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Singleton hook — owns the open state + the global Cmd+K / Ctrl+K key
 * binding. Mount once at the route level; the palette component reads
 * ``open`` + ``onOpenChange`` from the same hook.
 *
 * Thin adapter over the generic ``useCommandPaletteShortcut`` so the
 * existing callers (``_authed/route.tsx``) don't have to change shape.
 */
export function useChatCommandPalette(): {
  open: boolean;
  setOpen: (v: boolean) => void;
} {
  const controller: CommandPaletteController = useGenericCommandPaletteShortcut();
  return { open: controller.open, setOpen: controller.setOpen };
}

// ─── Documents lane helpers ────────────────────────────────────────────────

/**
 * Map a document MIME type to the Lucide icon component we render in the
 * palette row. Falls back to ``FileText`` for unknown / null mimes — the
 * row still renders cleanly with the generic icon.
 */
function pickDocIcon(
  mime: string | null,
): typeof FileText | typeof FileSpreadsheet | typeof Presentation {
  if (!mime) return FileText;
  if (
    mime === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    mime === "application/vnd.ms-excel" ||
    mime === "application/vnd.oasis.opendocument.spreadsheet" ||
    mime === "text/csv"
  ) {
    return FileSpreadsheet;
  }
  if (
    mime === "application/vnd.openxmlformats-officedocument.presentationml.presentation" ||
    mime === "application/vnd.ms-powerpoint" ||
    mime === "application/vnd.oasis.opendocument.presentation"
  ) {
    return Presentation;
  }
  return FileText;
}

export const ConversationCommandPalette: FC<ConversationCommandPaletteProps> = ({
  open,
  onOpenChange,
}) => {
  const navigate = useNavigate();
  const { i18n } = useLingui();

  // Chat-side state (existing lanes — conversations + actions).
  const conversations = useChatStore((s) => s.conversations);
  const orderedIds = useChatStore((s) => s.orderedIds);
  const currentId = useChatStore(selectCurrentConversationId);
  const selectConversation = useChatStore((s) => s.selectConversation);
  const setTitle = useChatStore((s) => s.setTitle);
  const setComposerDraft = useChatStore((s) => s.setComposerDraft);

  const { projects } = useSidebarProjects();
  const setCurrentProjectId = useProjectsStore(selectSetCurrentProjectId);
  const { create } = useCreateConversation();
  const exportMutation = useExportConversation();
  const createManual = useCreateManualAction();
  const manualActionCatalog = useManualActionCatalog();

  // Documents lane — top 20 recenti via the existing workspace hook. The
  // hook polls every 30s anyway so opening the palette right after an
  // upload still surfaces the new doc. ``cmdk`` does the fuzzy scoring
  // client-side — fast enough at 20 rows, zero BE round-trip per keystroke.
  const { documents } = useDocuments({ limit: 20 });

  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  // Local query — controlled here so the primitive stays stateless. The
  // primitive resets the value on close, so the next ⌘K open starts fresh.
  const [query, setQuery] = useState("");

  // ── Build groups ────────────────────────────────────────────────────────

  const actionItems = useMemo<ReadonlyArray<CommandPaletteItem>>(() => {
    const items: CommandPaletteItem[] = [
      {
        id: "action:new-chat",
        label: i18n._(msg`Nuova chat`),
        icon: Plus,
        keywords: ["nuova", "chat", "new", "create"],
        onSelect: () => {
          const id = create();
          selectConversation(id);
        },
      },
    ];
    if (currentId) {
      items.push({
        id: "action:rename-current",
        label: i18n._(msg`Rinomina conversazione corrente`),
        icon: Pencil,
        keywords: ["rinomina", "rename", "titolo"],
        onSelect: () => {
          const existing = conversations[currentId]?.title ?? "";
          // Defer the prompt until the palette has closed so focus return
          // works cleanly (Radix shifts focus back to the trigger first).
          queueMicrotask(async () => {
            const next = await prompt({
              title: i18n._(msg`Rinomina conversazione`),
              label: i18n._(msg`Nuovo titolo`),
              initialValue: existing,
              minLength: 1,
            });
            if (next !== null && next.trim().length > 0) {
              setTitle(currentId, next.trim());
            }
          });
        },
      });
      items.push({
        id: "action:export-current",
        label: i18n._(msg`Esporta conversazione corrente`),
        icon: Download,
        keywords: ["esporta", "export", "markdown", "download"],
        onSelect: () => {
          exportMutation.mutate(currentId);
        },
      });
    }
    return items;
  }, [create, selectConversation, currentId, conversations, setTitle, exportMutation, i18n]);

  // "Nuova azione" — start any action by hand from the working area. Each
  // row mints a manual proposal + opens its working-area surface (the hook
  // navigates on success). DRY: rows come from the shared catalog so adding
  // an action surfaces it here AND in the per-area shortcuts at once.
  const manualActionItems = useMemo<ReadonlyArray<CommandPaletteItem>>(
    () =>
      manualActionCatalog.map((def) => ({
        id: def.id,
        label: def.label,
        icon: def.icon,
        keywords: def.keywords ? [...def.keywords] : undefined,
        onSelect: () => {
          createManual.mutate(def.input);
        },
      })),
    [createManual, manualActionCatalog],
  );

  const documentItems = useMemo<ReadonlyArray<CommandPaletteItem>>(() => {
    return documents.map((d) => {
      const Icon = pickDocIcon(d.mime_type);
      const relative = formatRelativeTime(d.updated_at ?? d.created_at);
      return {
        id: `doc:${d.doc_id}`,
        label: d.filename,
        hint: relative || undefined,
        icon: Icon,
        keywords: [d.mime_type ?? "", d.source_type, d.title],
        // Primary: open the editor in the Documents hub via ``?focus=``.
        // The hub's existing ``focusId`` branch (validateWorkspaceSearch)
        // takes over and mounts the inline editor for ``ArtifactPane``.
        onSelect: () => {
          void navigate({
            to: "/workspace/documents",
            search: { focus: d.doc_id },
          });
        },
        // Alt (⌘+Enter): prefill the chat composer with a modify-this-doc
        // draft so the user can refine the prompt before hitting send. The
        // BE Phase 3.1 ``resolve_document`` tool still runs when the prompt
        // is submitted — the prefill is a UX shortcut, not a side-channel.
        onAltSelect: () => {
          setComposerDraft(i18n._(msg`modifica ${d.filename}: `));
        },
      };
    });
  }, [documents, navigate, setComposerDraft, i18n]);

  const conversationItems = useMemo<ReadonlyArray<CommandPaletteItem>>(() => {
    return orderedIds
      .map((id) => conversations[id])
      .filter((c): c is NonNullable<typeof c> => Boolean(c))
      .map((c) => {
        const title = c.title.trim().length > 0 ? c.title : i18n._(msg`Senza titolo`);
        return {
          id: `conv:${c.id}`,
          label: title,
          icon: MessageSquare,
          keywords: [title],
          trailing:
            c.id === currentId ? (
              <span className="ml-auto text-[10px] text-ando-orange">
                {i18n._(msg`Attiva`)}
              </span>
            ) : null,
          onSelect: () => {
            selectConversation(c.id);
          },
        };
      });
  }, [orderedIds, conversations, currentId, selectConversation, i18n]);

  const projectItems = useMemo<ReadonlyArray<CommandPaletteItem>>(() => {
    return projects.map((p) => ({
      id: `project:${p.project_id}`,
      label: p.name,
      icon: Folder,
      keywords: [p.name],
      onSelect: () => {
        setCurrentProjectId(p.project_id);
      },
    }));
  }, [projects, setCurrentProjectId]);

  // CRM navigation lane (Jul 9, 2026 — sales_crm_plan Phase 3): jump straight
  // to a Sales sub-tab. Same navigate-with-search pattern as the documents
  // lane; the `sub` param is handled by validateSalesSearch + SubTabBar.
  const crmNavItems = useMemo<ReadonlyArray<CommandPaletteItem>>(
    () =>
      (
        [
          { sub: "pipeline", label: i18n._(msg`Vai alla pipeline`), keywords: ["pipeline", "deal", "opportunità", "kanban", "vendite"] },
          { sub: "contacts", label: i18n._(msg`Vai ai contatti`), keywords: ["contatti", "persone", "crm", "rubrica"] },
          { sub: "companies", label: i18n._(msg`Vai alle aziende`), keywords: ["aziende", "account", "clienti", "crm"] },
          { sub: "campaigns", label: i18n._(msg`Vai alle campagne`), keywords: ["campagne", "outbound", "prospecting", "lead"] },
        ] as const
      ).map((entry) => ({
        id: `crm-nav:${entry.sub}`,
        label: entry.label,
        icon: TrendingUp,
        keywords: [...entry.keywords],
        onSelect: () => {
          void navigate({ to: "/workspace/sales", search: { sub: entry.sub } });
        },
      })),
    [navigate, i18n],
  );

  const groups = useMemo<ReadonlyArray<CommandPaletteGroup>>(
    () => [
      { id: "actions", heading: i18n._(msg`Azioni rapide`), items: actionItems },
      { id: "manual-actions", heading: i18n._(msg`Nuova azione`), items: manualActionItems },
      { id: "crm-nav", heading: i18n._(msg`CRM`), items: crmNavItems },
      { id: "documents", heading: i18n._(msg`Documenti`), items: documentItems },
      { id: "conversations", heading: i18n._(msg`Chat recenti`), items: conversationItems },
      { id: "projects", heading: i18n._(msg`Progetti`), items: projectItems },
    ],
    [actionItems, manualActionItems, crmNavItems, documentItems, conversationItems, projectItems, i18n],
  );

  // Footer hint — surfaces the ⌘+Enter affordance so the user discovers
  // the doc-prefill shortcut without reading docs. Conditional on the
  // documents lane being non-empty so we don't tease a shortcut that
  // wouldn't trigger anywhere.
  const footer =
    documentItems.length > 0 ? (
      <div className="flex items-center justify-between gap-2">
        <span>
          <kbd className="rounded border border-hairline px-1.5 py-0.5 font-mono text-[10px] text-ink-3">
            ⌘ + Enter
          </kbd>{" "}
          {i18n._(msg`sui documenti per modificare via chat`)}
        </span>
      </div>
    ) : null;

  return (
    <CommandPalette
      isOpen={open}
      onClose={close}
      query={query}
      onQueryChange={setQuery}
      placeholder={i18n._(msg`Cerca documenti, chat, azioni…`)}
      emptyMessage={i18n._(msg`Nessun risultato`)}
      groups={groups}
      ariaLabel={i18n._(msg`Comandi rapidi`)}
      footer={footer}
    />
  );
};

ConversationCommandPalette.displayName = "ConversationCommandPalette";
